import type { PropDoc } from '@/runtime/shared/elements-definition';
import type { Colors, Variants } from 'types';

export interface MenuItemProps {
  /**
   * Whether the menu item is selected.
   * @defaultValue false
   */
  selected?: boolean;
  /**
   * The label of the MenuItem
   */
  label?: string;
  /**
   * The description of the MenuItem
   */
  description?: string;
  /**
   * The icon that will be displayed in the leading slot.
   */
  icon?: string;
  /**
   * The icon that will be displayed in the trailing slot.
   */
  trailingIcon?: string;
  /**
   * The external icon that is shown when target is `_blank`.
   * @defaultValue e-menu's externalIcon
   */
  externalIcon?: string;
  /**
   * The type of the item.
   * The `label` type is only displayed in `vertical` orientation.
   * The `trigger` type is used to force the item to be collapible when its a link in `vertical` orientation.
   * @defaultValue 'link'
   */
  type?: 'label' | 'trigger' | 'link';
  /**
   * Where to display the linked URL, as the name for a browsing context.
   */
  target?: '_blank' | '_parent' | '_self' | '_top' | (string & {}) | null;
  /**
   * The URL that the menu item should navigate to.
   */
  to?: string;
  /**
   * The children of the item.
   */
  children?: MenuItemProps[];
  /**
   * If this item should be open by default if it has children.
   */
  open?: boolean;
  /**
   * Whether the menu is in collapsed state. Passed down from parent menu.
   */
  collapsed?: boolean;
  /**
   * If 'true', the item will be disabled.
   */
  disabled?: boolean;
  /**
   * The color of the menu item.
   * @defaultValue `neutral`
   */
  color?: Colors;
  /**
   * The variant of the menu item.
   * @defaultValue `soft`
   */
  variant?: Variants;
  /**
   * Additional classes to apply to the menu item.
   */
  class?: string;
}

export interface MenuProps<T extends MenuItemProps> {
  /**
   * The element or component this component should render as.
   * @defaultValue `nav`
   */
  as?: unknown;

  /**
   * The external icon that will be displayed when target is `_blank`.
   * @defaultValue `export-outline`
   */
  externalIcon?: string;
  /**
   * A list of menu items
   */
  items?: T[];
  /**
   * When `false`, the logo will be hidden.
   * You can also pass props of the e-logo element.
   * @defaultValue true
   */
  logo?: boolean;
  /**
   * Weather the vertical navigation menu is collapsed.
   * Defining a boolean will force you to create your own logic!
   * @defaultValue true
   */
  collapsible?: boolean;
  /**
   * Weather the mobile navigation menu is open.
   * Defining a boolean will force you to create your own logic!
   */
  open?: boolean;
  class?: string;
}

export interface MenuSlots extends Record<string, unknown> {
  /**
   * Contains the menu logo and open/close button on mobile.
   */
  header?: unknown;
  /**
   * Will be positioned before the list (default) slot.
   */
  leading?: unknown;
  /**
   * Contains the lest of menu-items.
   */
  default?: unknown;
  /**
   * Will be positioned after the list (default) slot.
   */
  trailing?: unknown;
  /**
   * Contains the options of the menu. Like appearance and localisation.
   */
  footer?: unknown;
}

export interface MenuItemSlots extends Record<string, unknown> {
  /**
   * Contains the leading icon by default.
   */
  leading?: unknown;
  /**
   * Contains the label.
   */
  default?: unknown;
  /**
   * Contains the trailing icon by default.
   * When it has children, trailingIcon will be `chevron-right`.
   * When target is `_blank`, trailingIcon will be `open-in-new`.
   */
  trailing?: unknown;
  /**
   * Contains the child items by default.
   * When children has length or this slot contains content, The MenuItem will be collapsible.
   */
  children?: unknown;
}

type MenuPropKeys = keyof MenuProps<MenuItemProps>;
type MenuItemPropKeys = keyof MenuItemProps;

/**
 * API docs mapping for `<e-menu>` props.
 *
 * Keys come from {@link MenuProps}, values are used by the docs renderer
 * to build the props table.
 */
export const menuPropsDocs: Record<MenuPropKeys, PropDoc> = {
  as: {
    name: 'as',
    type: 'string',
    defaultValue: 'nav',
    descriptionKey: 'menu.as.description'
  },
  externalIcon: {
    name: 'externalIcon',
    type: 'string',
    defaultValue: 'export-ouline',
    descriptionKey: 'menu.externalIcon.description'
  },
  items: {
    name: 'items',
    type: 'MenuItemProps[]',
    descriptionKey: 'menu.items.description'
  },
  logo: {
    name: 'logo',
    type: 'boolean',
    defaultValue: 'true',
    descriptionKey: 'menu.logo.description'
  },
  collapsible: {
    name: 'collapsible',
    type: 'boolean',
    defaultValue: 'true',
    descriptionKey: 'menu.collapsible.description'
  },
  open: {
    name: 'open',
    type: 'boolean',
    defaultValue: 'true',
    descriptionKey: 'menu.open.description'
  },
  class: {
    name: 'class',
    type: 'string',
    descriptionKey: 'menu.class.description'
  }
} as const;

/**
 * API docs mapping for `<e-menu-item>` props.
 *
 * Keys come from {@link MenuItemProps}, values are used by the docs
 * renderer to build the props table.
 */
export const menuItemPropsDocs: Record<MenuItemPropKeys, PropDoc> = {
  selected: {
    name: 'selected',
    type: 'string',
    required: true,
    descriptionKey: 'selected.item.value.description'
  },
  label: {
    name: 'label',
    type: 'boolean',
    defaultValue: 'false',
    descriptionKey: 'menu.item.label.description'
  },
  description: {
    name: 'description',
    type: 'string',
    descriptionKey: 'menu.item.description.description'
  },
  icon: {
    name: 'icon',
    type: 'string',
    descriptionKey: 'menu.item.icon.description'
  },
  trailingIcon: {
    name: 'trailingIcon',
    type: 'string',
    descriptionKey: 'menu.item.trailingIcon.description'
  },
  externalIcon: {
    name: 'externalIcon',
    type: 'string',
    defaultValue: 'export-outline',
    descriptionKey: 'menu.item.externalIcon.description'
  },
  type: {
    name: 'type',
    type: 'label | trigger | link',
    descriptionKey: 'menu.item.type.description'
  },
  target: {
    name: 'target',
    type: '_blank | _parent | _self | _top | (string & {}) | null',
    descriptionKey: 'menu.item.target.description'
  },
  to: {
    name: 'to',
    type: 'string',
    descriptionKey: 'menu.item.to.description'
  },
  children: {
    name: 'children',
    type: 'MenuItemProps[]',
    descriptionKey: 'menu.item.children.description'
  },
  open: {
    name: 'open',
    type: 'boolean',
    descriptionKey: 'menu.item.open.description'
  },
  collapsed: {
    name: 'collapsed',
    type: 'boolean',
    descriptionKey: 'menu.item.collapsed.description'
  },
  disabled: {
    name: 'disabled',
    type: 'boolean',
    descriptionKey: 'menu.item.disabled.description'
  },
  color: {
    name: 'color',
    type: 'string',
    descriptionKey: 'menu.item.color.description'
  },
  variant: {
    name: 'variant',
    type: 'string',
    descriptionKey: 'menu.item.variant.description'
  },
  class: {
    name: 'class',
    type: 'string',
    descriptionKey: 'menu.item.class.description'
  }
} as const;
